import { getNumDaysSinceEpoch } from './utils.js';
import { GameType } from '../types/GameType.ts';
import { getGameConfig, WeaverGameConfig, SubtextGameConfig } from '../types/GameConfig.ts';
import { getTotalPointsFromWords, getRankFromWords, RANK_LEVELS, FOUND_ALL_WORDS_HIDDEN_RANK_NAME } from '../games/subtext/progressLevels.ts';
import { syncGameStatsToCloud } from '../supabase/syncAdapter.ts';

/**
 * User Stats Utils
 * 
 * This module handles saving and retrieving user statistics from localStorage,
 * including game completion stats, streaks, and best scores. It supports multiple
 * game types while maintaining backwards compatibility with the original Weaver game.
 * 
 * Storage Format:
 * - Original Weaver (backwards compatibility):
 *   - userStats: {
 *     - numSolved: number        // total number of games solved (excludes archived games)
 *     - lastSolvedEpochDayStats: {  // only recorded for last "today" solved
 *       - epochDay: number
 *       - minPathLength: number
 *       - minPathWords: string[]
 *     }
 *     - currentStreak: number    // excludes archived games
 *     - longestStreak: number    // excludes archived games
 *   }
 *   - gameStats: {
 *     [gameNumber: number]: {
 *       - minPathLength: number
 *       - minPathWords: string[]
 *     }
 *   }
 * 
 * - All Game Types (including Weaver):
 *   - userStats_<gameType>: { ... }  // same structure as above
 *   - gameStats_<gameType>: { ... }  // same structure as above
 * 
 * Migration Strategy:
 * - For GameType.WEAVER: Read from new format first, fall back to old format and migrate
 * - For GameType.WEAVER: Write to both new and old formats
 * - For other game types: Use new format only
 */

// Weaver-specific extensions
export interface WeaverGameStats {
  minPathLength: number | null;
  minPathWords: string[] | null;
}

export interface WeaverLastSolvedStats {
  epochDay: number;
  minPathLength: number;
  minPathWords: string[];
}

export interface WeaverUserStats {
  numSolved: number;
  currentStreak: number;
  longestStreak: number;
  lastSolvedEpochDayStats: WeaverLastSolvedStats | null;
}

export interface SubtextGameStats {
  words: string[];
}

export interface SubtextUserStats {
  totalGamesPlayed: number;
  totalWordsFound: number;
  highRankCount: number;
  highRankName: string;
}

export type GameStats = WeaverGameStats | SubtextGameStats;

interface GameStatsMap {
  [gameNumber: number]: GameStats;
}

export interface WeaverDisplayStats extends WeaverUserStats {
  minPathLength: number | null;
  optimalPathLength: number;
  startWord: string;
  endWord: string;
  minPathWords: string[] | null;
}

export interface SubtextDisplayStats extends SubtextUserStats {
  // Today's game stats
  todayWordCount: number | null;
  todayPoints: number | null;
  todayRank: string | null;
  foundAllWords: boolean;
  foundAllWordsRankName: string;

  dailyWord: string;
  letterCounts: Record<string, number>;
}

// Union type for all display stats
type DisplayStats = WeaverDisplayStats | SubtextDisplayStats;

// Helper functions for deriving Subtext stats from stored words
function subtextCalculateLetterCounts(words: string[], dailyWord: string): Record<string, number> {
  const letterCounts: Record<string, number> = {};

  // Initialize all letters in daily word to 0
  for (const letter of dailyWord) {
    letterCounts[letter] = 0;
  }

  // Count words by first letter
  words.forEach(word => {
    const firstLetter = word[0];
    if (firstLetter && letterCounts.hasOwnProperty(firstLetter)) {
      letterCounts[firstLetter]++;
    }
  });

  return letterCounts;
}

function subtextHasFoundAllWords(words: string[], validSubWords: string[]): boolean {
  return words.length >= validSubWords.length;
}

function subtextDeriveUserStats(): SubtextUserStats {
  const gameType = GameType.SUBTEXT;
  const key = `gameStats_${gameType}`;
  const stored = localStorage.getItem(key);

  if (!stored) {
    return getDefaultSubtextUserStats();
  }

  try {
    const allGameStats = JSON.parse(stored) as Record<number, SubtextGameStats>;
    let totalWordsFound = 0;
    let highRankCount = 0;

    const gameConfig = getGameConfig(gameType) as SubtextGameConfig;
    const maxRankId = RANK_LEVELS.length - 1; // Highest rank ID

    for (const [gameNumberStr, gameStats] of Object.entries(allGameStats)) {
      const gameNumber = parseInt(gameNumberStr);
      const gameData = gameConfig.getGameData(gameNumber);

      if (gameData && gameStats.words) {
        totalWordsFound += gameStats.words.length;
        const rank = getRankFromWords(gameStats.words, gameData.realisticMaxScore, gameData.word, gameData.longestSubWordLength);
        const hasFoundAllWords = subtextHasFoundAllWords(gameStats.words, gameData.validSubWords);

        // Count games where user achieved the highest rank or found all words
        if (rank.id === maxRankId || hasFoundAllWords) {
          highRankCount++;
        }
      }
    }

    return {
      totalGamesPlayed: Object.keys(allGameStats).length,
      totalWordsFound,
      highRankCount,
      highRankName: RANK_LEVELS[maxRankId].name,
    };
  } catch (error) {
    return getDefaultSubtextUserStats();
  }
}

// Function overloads for strong typing
export function getDisplayStatsForGameNumber(gameType: GameType.WEAVER | GameType.WEAVER_X, gameNumber: number): WeaverDisplayStats;
export function getDisplayStatsForGameNumber(gameType: GameType.SUBTEXT, gameNumber: number): SubtextDisplayStats;
export function getDisplayStatsForGameNumber(gameType: GameType, gameNumber: number): DisplayStats;

export function getDisplayStatsForGameNumber(gameType: GameType, gameNumber: number): DisplayStats {
  const gameConfig = getGameConfig(gameType);

  // Handle different game types
  if (gameType === GameType.WEAVER || gameType === GameType.WEAVER_X) {
    const userStats = getWeaverUserStats(gameType);
    const lastSolvedEpochDayStats = userStats.lastSolvedEpochDayStats;
    const weaverConfig = gameConfig as WeaverGameConfig;
    const wordPair = weaverConfig.getGameData(gameNumber);
    const weaverUserStats = userStats as WeaverUserStats;

    // Handle case where word pair is not available
    if (!wordPair) {
      return {
        numSolved: weaverUserStats.numSolved,
        minPathLength: null,
        optimalPathLength: 0,
        startWord: '',
        endWord: '',
        minPathWords: null,
        currentStreak: 0,
        longestStreak: weaverUserStats.longestStreak,
        lastSolvedEpochDayStats: weaverUserStats.lastSolvedEpochDayStats
      } as WeaverDisplayStats;
    }

    // Now we know wordPair is a WordPair type
    const { startWord, endWord, optimalPathLength } = wordPair;
    const minPathWords = getWeaverGameNumberSolvedMinPathWords(gameType, gameNumber);
    const minPathLength = minPathWords != null ? minPathWords.length : null;
    return {
      numSolved: weaverUserStats.numSolved,
      minPathLength,
      optimalPathLength,
      startWord,
      endWord,
      minPathWords,
      currentStreak:
        lastSolvedEpochDayStats != null && lastSolvedEpochDayStats.epochDay >= (getNumDaysSinceEpoch() - 1) ?
          weaverUserStats.currentStreak :
          0,
      longestStreak: weaverUserStats.longestStreak,
      lastSolvedEpochDayStats: weaverUserStats.lastSolvedEpochDayStats
    } as WeaverDisplayStats;
  } else if (gameType === GameType.SUBTEXT) {
    // Handle Subtext game type
    const subtextConfig = gameConfig as SubtextGameConfig;
    const gameData = subtextConfig.getGameData(gameNumber);

    if (gameData == null) {
      return {
        ...getDefaultSubtextUserStats(),
        todayWordCount: null,
        todayPoints: null,
        todayRank: null,
        foundAllWords: false,
        foundAllWordsRankName: FOUND_ALL_WORDS_HIDDEN_RANK_NAME,
        dailyWord: '',
        letterCounts: {},
      } as SubtextDisplayStats;
    }

    // Get the daily word from game data
    const dailyWord = gameData.word;

    // Get stored game stats for this specific game number
    const gameStats = getGameNumberStats(gameType, gameNumber) as SubtextGameStats;
    const wordsFound = gameStats.words || [];

    // Derive today's stats from stored words
    const todayWordCount = wordsFound.length > 0 ? wordsFound.length : null;
    const todayPoints = wordsFound.length > 0 ? getTotalPointsFromWords(wordsFound, dailyWord, gameData.longestSubWordLength) : null;
    const todayRank = wordsFound.length > 0 ?
      getRankFromWords(wordsFound, gameData.realisticMaxScore, dailyWord, gameData.longestSubWordLength).name : null;
    const foundAllWords = wordsFound.length > 0 ? subtextHasFoundAllWords(wordsFound, gameData.validSubWords) : false;

    // Calculate letter counts for sharing
    const letterCounts = subtextCalculateLetterCounts(wordsFound, dailyWord);

    // Get derived historical stats
    const deriveUserdStats = subtextDeriveUserStats();

    return {
      // Today's game stats - derived from stored words
      todayWordCount,
      todayPoints,
      todayRank,
      foundAllWords,
      foundAllWordsRankName: FOUND_ALL_WORDS_HIDDEN_RANK_NAME,

      // Today's game data for sharing
      dailyWord,
      letterCounts,

      // Historical stats - derived from all game stats
      ...deriveUserdStats,
    } as SubtextDisplayStats;
  }

  // Fallback - should not reach here with current game types
  throw new Error(`Unsupported game type: ${gameType}`);
}

function getWeaverGameNumberSolvedMinPathWords(gameType: GameType.WEAVER | GameType.WEAVER_X, gameNumber: number): string[] | null {
  const gameNumberStats = getGameNumberStats(gameType, gameNumber) as WeaverGameStats;
  const gameNumberStatsMinPathWords = gameNumberStats.minPathWords;
  if (gameNumberStatsMinPathWords != null) {
    return gameNumberStatsMinPathWords;
  }
  const gameConfig = getGameConfig(gameType);
  const isTodaysGame = gameConfig.isGameNumberCurrentGame(gameNumber);
  if (!isTodaysGame) {
    return null;
  }
  const userStats = getWeaverUserStats(gameType);
  const lastSolvedEpochDayStats = userStats.lastSolvedEpochDayStats;
  if (lastSolvedEpochDayStats == null) {
    return null;
  }
  const lastSolvedEpochDay = lastSolvedEpochDayStats.epochDay;
  const gameNumberEpochDay = gameConfig.getEpochDayFromGameNumber(gameNumber);
  if (gameNumberEpochDay !== lastSolvedEpochDay) {
    return null;
  }
  return lastSolvedEpochDayStats.minPathWords;
}

export function getHasSolvedGameNumber(gameType: GameType, gameNumber: number): boolean {
  const gameNumberStats = getGameNumberStats(gameType, gameNumber);

  // Check if game has been solved based on game type
  if (gameType === GameType.SUBTEXT) {
    // You don't really "solve" Subtext games. We could return true if
    // the user has found all words, or the highest rank, but that's not
    // needed right now.
    return false;
  } else {
    const weaverStats = gameNumberStats as WeaverGameStats;
    if (weaverStats.minPathWords != null) {
      return true;
    }
  }

  const gameConfig = getGameConfig(gameType);
  const isTodaysGame = gameConfig.isGameNumberCurrentGame(gameNumber);
  if (!isTodaysGame) {
    return false;
  }
  const userStats = getWeaverUserStats(gameType);
  const lastSolvedEpochDayStats = userStats.lastSolvedEpochDayStats;
  if (lastSolvedEpochDayStats == null) {
    return false;
  }
  const lastSolvedEpochDay = lastSolvedEpochDayStats.epochDay;
  const gameNumberEpochDay = gameConfig.getEpochDayFromGameNumber(gameNumber);
  return gameNumberEpochDay === lastSolvedEpochDay;
}

function getIsNewPathShorterThanExistingMinPath(gameType: GameType, gameNumber: number, newPathLength: number): boolean {
  // Only applicable to Weaver games
  if (gameType !== GameType.WEAVER && gameType !== GameType.WEAVER_X) {
    return false; // Not applicable for non-Weaver games
  }

  const gameNumberStats = getGameNumberStats(gameType, gameNumber) as WeaverGameStats;
  if (gameNumberStats.minPathLength != null) {
    return newPathLength < gameNumberStats.minPathLength;
  }
  return true;
}

function getDefaultWeaverUserStats(): WeaverUserStats {
  return {
    numSolved: 0,
    lastSolvedEpochDayStats: null,
    currentStreak: 0,
    longestStreak: 0,
  };
}


// Weaver-specific default game stats
function getDefaultWeaverGameStats(): WeaverGameStats {
  return {
    minPathLength: null,
    minPathWords: null,
  };
}

function getDefaultSubtextUserStats(): SubtextUserStats {
  const maxRankId = RANK_LEVELS.length - 1; // Highest rank ID
  return {
    totalGamesPlayed: 0,
    totalWordsFound: 0,
    highRankCount: 0,
    highRankName: RANK_LEVELS[maxRankId].name,
  };
}

// Subtext-specific default game stats
function getDefaultSubtextGameStats(): SubtextGameStats {
  return {
    words: [],
  };
}

// Get default game stats for any game type
function getDefaultGameStats(gameType: GameType): GameStats {
  switch (gameType) {
    case GameType.SUBTEXT:
      return getDefaultSubtextGameStats();
    case GameType.WEAVER:
    case GameType.WEAVER_X:
      return getDefaultWeaverGameStats();
    default:
      throw new Error(`Unsupported game type: ${gameType}`);
  }
}

export function getWeaverUserStats(gameType: GameType.WEAVER | GameType.WEAVER_X): WeaverUserStats {
  if (gameType === GameType.WEAVER) {
    // Try new format first
    const newFormat = localStorage.getItem(`userStats_${gameType}`);
    if (newFormat !== null) {
      try {
        return JSON.parse(newFormat);
      } catch (error) {
        // If new format has invalid JSON, return default values
        return getDefaultWeaverUserStats();
      }
    }
    // Fall back to old format and migrate
    const oldFormat = localStorage.getItem('userStats');
    if (oldFormat !== null) {
      try {
        const parsed = JSON.parse(oldFormat);
        // Migrate to new format
        localStorage.setItem(`userStats_${gameType}`, oldFormat);
        return parsed;
      } catch (error) {
        // If old format has invalid JSON, return default values
        return getDefaultWeaverUserStats();
      }
    }
    return getDefaultWeaverUserStats();
  }

  const key = `userStats_${gameType}`;
  const stats = localStorage.getItem(key);
  if (stats) {
    try {
      return JSON.parse(stats);
    } catch (error) {
      // If stored data has invalid JSON, return default values
      return getDefaultWeaverUserStats();
    }
  }
  return getDefaultWeaverUserStats();
}

export function setWeaverUserStats(gameType: GameType, stats: WeaverUserStats): void {
  // Always write to new format
  localStorage.setItem(`userStats_${gameType}`, JSON.stringify(stats));

  // Also write to old format for Weaver (backwards compatibility)
  if (gameType === GameType.WEAVER) {
    localStorage.setItem('userStats', JSON.stringify(stats));
  }
}

export function getGameNumberStats(gameType: GameType, gameNumber: number): GameStats {
  if (gameType === GameType.WEAVER) {
    // Try new format first
    const newFormat = localStorage.getItem(`gameStats_${gameType}`);
    if (newFormat !== null) {
      try {
        const gameStats = JSON.parse(newFormat) as GameStatsMap;
        const gameNumberStats = gameStats[gameNumber];
        if (gameNumberStats == null) {
          return getDefaultWeaverGameStats();
        }
        return gameNumberStats;
      } catch (error) {
        // If new format has invalid JSON, return default values
        return getDefaultWeaverGameStats();
      }
    }
    // Fall back to old format and migrate
    const oldFormat = localStorage.getItem('gameStats');
    if (oldFormat !== null) {
      try {
        const gameStats = JSON.parse(oldFormat) as GameStatsMap;
        const gameNumberStats = gameStats[gameNumber];
        if (gameNumberStats == null) {
          return getDefaultWeaverGameStats();
        }
        // Migrate to new format
        localStorage.setItem(`gameStats_${gameType}`, oldFormat);
        return gameNumberStats;
      } catch (error) {
        // If old format has invalid JSON, return default values
        return getDefaultWeaverGameStats();
      }
    }
    return getDefaultWeaverGameStats();
  }

  const key = `gameStats_${gameType}`;
  const stored = localStorage.getItem(key);
  if (stored) {
    try {
      const gameStats = JSON.parse(stored) as GameStatsMap | null;
      if (gameStats == null) {
        return getDefaultGameStats(gameType);
      }
      const gameNumberStats = gameStats[gameNumber];
      if (gameNumberStats == null) {
        return getDefaultGameStats(gameType);
      }
      return gameNumberStats;
    } catch (error) {
      // If stored data has invalid JSON, return default values
      return getDefaultGameStats(gameType);
    }
  }
  return getDefaultGameStats(gameType);
}

function setWeaverGameNumberStats(gameType: GameType.WEAVER | GameType.WEAVER_X, gameNumber: number, enteredWords: string[]): void {
  if (gameType === GameType.WEAVER) {
    // Write to new format
    const newKey = `gameStats_${gameType}`;
    let newGameStats: GameStatsMap | null = null;
    try {
      newGameStats = JSON.parse(localStorage.getItem(newKey) || 'null') as GameStatsMap | null;
    } catch (error) {
      // If invalid JSON, start with null
      newGameStats = null;
    }
    const updatedNewGameStats = newGameStats != null ? getObjectDeepCopy(newGameStats) : {};
    updatedNewGameStats[gameNumber] = {
      minPathLength: enteredWords.length,
      minPathWords: enteredWords,
    };
    localStorage.setItem(newKey, JSON.stringify(updatedNewGameStats));

    // Also write to old format (backwards compatibility)
    let oldGameStats: GameStatsMap | null = null;
    try {
      oldGameStats = JSON.parse(localStorage.getItem('gameStats') || 'null') as GameStatsMap | null;
    } catch (error) {
      // If invalid JSON, start with null
      oldGameStats = null;
    }
    const updatedOldGameStats = oldGameStats != null ? getObjectDeepCopy(oldGameStats) : {};
    updatedOldGameStats[gameNumber] = {
      minPathLength: enteredWords.length,
      minPathWords: enteredWords,
    };
    localStorage.setItem('gameStats', JSON.stringify(updatedOldGameStats));
    syncGameStatsToCloud(gameType, gameNumber, { minPathLength: enteredWords.length, minPathWords: enteredWords });
    return;
  }

  const key = `gameStats_${gameType}`;
  let gameStats: GameStatsMap | null = null;
  try {
    gameStats = JSON.parse(localStorage.getItem(key) || 'null') as GameStatsMap | null;
  } catch (error) {
    // If invalid JSON, start with null
    gameStats = null;
  }
  const newGameStats = gameStats != null ? getObjectDeepCopy(gameStats) : {};
  newGameStats[gameNumber] = {
    minPathLength: enteredWords.length,
    minPathWords: enteredWords,
  };
  localStorage.setItem(key, JSON.stringify(newGameStats));
  syncGameStatsToCloud(gameType, gameNumber, { minPathLength: enteredWords.length, minPathWords: enteredWords });
}

function getObjectDeepCopy<T>(object: T): T {
  return JSON.parse(JSON.stringify(object));
}

// Weaver-specific game completion logic
export function updateWeaverUserStatsWithWin(gameType: GameType.WEAVER | GameType.WEAVER_X, gameNumber: number, enteredWords: string[]): void {
  const pathLength = enteredWords.length;

  const gameConfig = getGameConfig(gameType);
  const isTodaysGame = gameConfig.isGameNumberCurrentGame(gameNumber);
  const gameNumberEpochDay = gameConfig.getEpochDayFromGameNumber(gameNumber);
  const existingUserStats = getWeaverUserStats(gameType);

  if (getHasSolvedGameNumber(gameType, gameNumber)) {
    if (getIsNewPathShorterThanExistingMinPath(gameType, gameNumber, pathLength)) {
      setWeaverGameNumberStats(gameType, gameNumber, enteredWords);
      if (isTodaysGame) {
        const newUserStats = getObjectDeepCopy(existingUserStats);
        newUserStats.lastSolvedEpochDayStats = {
          epochDay: gameNumberEpochDay,
          minPathLength: pathLength,
          minPathWords: enteredWords,
        };
        setWeaverUserStats(gameType, newUserStats);
      }
    }
  } else {
    setWeaverGameNumberStats(gameType, gameNumber, enteredWords);
    const newUserStats = getObjectDeepCopy(existingUserStats);
    if (isTodaysGame) {
      newUserStats.numSolved += 1;
      newUserStats.lastSolvedEpochDayStats = {
        epochDay: gameNumberEpochDay,
        minPathLength: pathLength,
        minPathWords: enteredWords,
      };
      const lastSolvedEpochDayStats = existingUserStats.lastSolvedEpochDayStats;
      if (lastSolvedEpochDayStats == null) {
        newUserStats.currentStreak = 1;
        newUserStats.longestStreak = 1;
      } else {
        const lastSolvedEpochDay = lastSolvedEpochDayStats.epochDay;
        const isStreakContinued = lastSolvedEpochDay === gameNumberEpochDay - 1;
        const newCurrentStreak = isStreakContinued ? existingUserStats.currentStreak + 1 : 1;
        newUserStats.currentStreak = newCurrentStreak;

        if (newCurrentStreak > existingUserStats.longestStreak) {
          newUserStats.longestStreak = newCurrentStreak;
        }
      }
    }
    setWeaverUserStats(gameType, newUserStats);
  }
}

// Subtext-specific game stats setter
export function setSubtextGameStats(gameNumber: number, wordsFound: string[]): void {
  const key = `gameStats_${GameType.SUBTEXT}`;
  let gameStats: Record<number, SubtextGameStats> | null = null;
  try {
    gameStats = JSON.parse(localStorage.getItem(key) || 'null') as Record<number, SubtextGameStats> | null;
  } catch (error) {
    gameStats = null;
  }
  const newGameStats = gameStats != null ? getObjectDeepCopy(gameStats) : {};
  newGameStats[gameNumber] = {
    words: wordsFound,
  };
  localStorage.setItem(key, JSON.stringify(newGameStats));
  syncGameStatsToCloud(GameType.SUBTEXT, gameNumber, { words: wordsFound });
}

// HowToPlay Modal Functions
export function getHasShownHowToPlay(gameType: GameType): boolean {
  return localStorage.getItem(`hasShownHowToPlay_${gameType}`) === 'true';
}

export function setHasShownHowToPlay(gameType: GameType): void {
  localStorage.setItem(`hasShownHowToPlay_${gameType}`, 'true');
}

export function shouldShowHowToPlayModal(gameType: GameType): boolean {
  const hasShownHowToPlay = getHasShownHowToPlay(gameType);

  switch (gameType) {
    case GameType.WEAVER:
    case GameType.WEAVER_X:
      const userStats = getWeaverUserStats(gameType);
      return !hasShownHowToPlay && userStats.numSolved === 0;
    case GameType.SUBTEXT:
      return !hasShownHowToPlay;
    default:
      throw new Error(`Unsupported game type: ${gameType}`);
  }
}

// Banner dismissal tracking
export function hasDismissedBanner(bannerId: string): boolean {
  return localStorage.getItem(`bannerDismissed_${bannerId}`) === 'true';
}

export function setBannerDismissed(bannerId: string): void {
  localStorage.setItem(`bannerDismissed_${bannerId}`, 'true');
}

// Notification tracking
export function hasSeenNotification(notificationId: string): boolean {
  return localStorage.getItem(`notificationSeen_${notificationId}`) === 'true';
}

export function setNotificationSeen(notificationId: string): void {
  localStorage.setItem(`notificationSeen_${notificationId}`, 'true');
}

// Weaver X loaded tracking
export function hasLoadedWeaverX(): boolean {
  return localStorage.getItem('has-loaded-weaver-x') === 'true';
}

export function setHasLoadedWeaverX(): void {
  localStorage.setItem('has-loaded-weaver-x', 'true');
}

// Subtext loaded tracking
export function hasLoadedSubtext(): boolean {
  return localStorage.getItem('has-loaded-subtext') === 'true';
}

export function setHasLoadedSubtext(): void {
  localStorage.setItem('has-loaded-subtext', 'true');
}